import { blue, green, grey, yellow } from "@flat-ui";
import { CompanyTypeKind } from "@custom-types/base-types";

interface CompanyTypeConfig {
  /** Label for the company type */
  label: string;

  /** Text color for the badge */
  color: string;

  /** Background color for the badge */
  backgroundColor: string;
}

/** Shared style for internal company types */
const INTERNAL_STYLE: Omit<CompanyTypeConfig, "label"> = {
  color: yellow[400],
  backgroundColor: yellow[50],
};

/**
 * Company type configuration with colors:
 * - Customer: Blue
 * - Partner/Distributor: Green
 * - Internal (R&D, Sales, Support): Yellow
 * - Unspecified: Gray (display only)
 */
export const COMPANY_TYPE_CONFIG: Record<CompanyTypeKind, CompanyTypeConfig> = {
  customer: {
    label: "Customer",
    color: blue[500],
    backgroundColor: blue[50],
  },
  ["partner_distributor"]: {
    label: "Partner / Distributor",
    color: green[500],
    backgroundColor: green[50],
  },
  ["internal_rnd"]: { label: "Internal R&D", ...INTERNAL_STYLE },
  ["internal_sales"]: { label: "Internal Sales", ...INTERNAL_STYLE },
  ["internal_support"]: { label: "Internal Support", ...INTERNAL_STYLE },
  unspecified: {
    label: "Unspecified",
    color: grey[500],
    backgroundColor: grey[100],
  },
};

/** The workspace types that FARO runs for itself, rather than for a paying customer */
const INTERNAL_COMPANY_TYPES: readonly CompanyTypeKind[] = [
  "internal_rnd",
  "internal_sales",
  "internal_support",
];

/** Whether the workspace belongs to FARO itself instead of to a customer */
export function isInternalCompanyType(
  type: CompanyTypeKind | undefined
): boolean {
  return type !== undefined && INTERNAL_COMPANY_TYPES.includes(type);
}
